/**
 * One schema per reference kind, all nine built from `REF_TABLE`. There are no
 * regexes in this file: `.docs/02-shared-core.md`, DRY watchlist — "One ref table
 * drives make, parse, validate and the zod schemas — do not write nine regexes."
 *
 * `ARCHITECTURE.md` §Naming: "Every tool addresses entities by human ref, never
 * UUID." These are the schemas that hold that line at the edge.
 */
import { z } from 'zod';
import { REF_TABLE } from '../refs.js';
function refSchema(kind, description) {
    const { pattern, example } = REF_TABLE[kind];
    return z
        .string()
        .regex(pattern, `Expected a ${kind} reference like ${example}.`)
        .describe(`${description} Case-sensitive, exactly as it is shown: ${example}.`);
}
export const InvoiceRefSchema = refSchema('invoice', 'An invoice reference, FES-YYYY-NNN. Sequential per year and gapless — it is the ' +
    'reference clients quote on a bank transfer, and Wise matching depends on it.');
export const WorkOrderRefSchema = refSchema('work_order', 'A work order reference, WO-YYYY-NNN. Work orders are projects; extras raised off the ' +
    'rate card get a REQ- reference instead.');
export const RequestRefSchema = refSchema('request', 'A request reference, REQ-NNN. A request is an extra: work outside the client’s package, ' +
    'priced off the rate card and billed in arrears after delivery.');
export const RetainerChangeRefSchema = refSchema('retainer_change', 'A package change reference, RET-CHG-NNN. A proposed change to a retainer’s blocks that ' +
    'takes effect on a billing day, never mid-period.');
export const ContentPlanRefSchema = refSchema('content_plan', 'A content plan reference, PLAN-YYYY-MM. One plan per client per period; no money ' +
    'attached, it is the calendar the client signs off.');
export const TaskRefSchema = refSchema('task', 'A task reference, TSK-NNNN. Tasks are the studio board and the agent queue.');
export const CallRefSchema = refSchema('call', 'A call reference, CALL-YYYY-NNN. One booked slot in the studio’s calendar.');
export const WidgetRefSchema = refSchema('widget', 'A widget reference, wgt_<type>_<hex4>. One widget instance on one client site, ' +
    'metering usage against its own cap.');
export const ClientSlugSchema = refSchema('client', 'A client slug, lowercase — 2 to 40 characters of letters, digits and hyphens. This is ' +
    'how every tool addresses a client; there is no UUID in the API surface.');
export const PostRefSchema = refSchema('post', 'A blog post reference, PST-YYYY-NNNN. One post on one client’s blog, whatever its slug ' +
    'becomes — the slug is the public address and can change; the reference cannot.');
export const MockupRefSchema = refSchema('mockup', 'A mockup reference, MCK-YYYY-NNN. One design shared with a client for feedback, across ' +
    'every version of it that has been uploaded.');
/** Every ref schema by kind, for anything that needs to pick one at runtime. */
export const REF_SCHEMAS = {
    invoice: InvoiceRefSchema,
    work_order: WorkOrderRefSchema,
    request: RequestRefSchema,
    retainer_change: RetainerChangeRefSchema,
    content_plan: ContentPlanRefSchema,
    task: TaskRefSchema,
    call: CallRefSchema,
    widget: WidgetRefSchema,
    client: ClientSlugSchema,
    post: PostRefSchema,
    mockup: MockupRefSchema,
};
//# sourceMappingURL=refs.js.map